/**
 * The futures HELM knows by root (watchlist search, Details tab). Static facts only — the live
 * `InstrumentInfo` from the md stream always wins for the contract, exchange, tick size and tick
 * value; a root ("NQ") is resolved to its front month by the gateway when it is watched.
 *
 * Tick sizes are rationals (points per tick = num / den) and tick values integer cents, as in
 * `FALLBACK_CONTRACTS` (brackets/model.ts). Sessions: CME Globex, Sunday–Friday, one daily break.
 */

export type CatalogGroup = "Equity index" | "Energy" | "Metals" | "Rates" | "FX" | "Ags" | "Crypto";

export type CatalogEntry = {
  root: string;
  name: string;
  exchange: "CME" | "CBOT" | "NYMEX" | "COMEX";
  group: CatalogGroup;
  tickSizeNum: number;
  tickSizeDen: number;
  /** Dollars per tick per contract (display only; ZN's 1/64 tick is .625). */
  tickValueUsd: number;
  /** Globex hours, Central Time. */
  session: string;
};

const EQ = "Sun–Fri 17:00–16:00 CT (RTH 08:30–15:00)";
const GLOBEX = "Sun–Fri 17:00–16:00 CT";
const AGS = "Sun–Fri 19:00–07:45, 08:30–13:20 CT";
const LIVESTOCK = "Mon–Fri 08:30–13:05 CT";

export const FUTURES_CATALOG: readonly CatalogEntry[] = [
  {
    root: "NQ",
    name: "E-mini Nasdaq-100",
    exchange: "CME",
    group: "Equity index",
    tickSizeNum: 1,
    tickSizeDen: 4,
    tickValueUsd: 5,
    session: EQ,
  },
  {
    root: "MNQ",
    name: "Micro E-mini Nasdaq-100",
    exchange: "CME",
    group: "Equity index",
    tickSizeNum: 1,
    tickSizeDen: 4,
    tickValueUsd: 0.5,
    session: EQ,
  },
  {
    root: "ES",
    name: "E-mini S&P 500",
    exchange: "CME",
    group: "Equity index",
    tickSizeNum: 1,
    tickSizeDen: 4,
    tickValueUsd: 12.5,
    session: EQ,
  },
  {
    root: "MES",
    name: "Micro E-mini S&P 500",
    exchange: "CME",
    group: "Equity index",
    tickSizeNum: 1,
    tickSizeDen: 4,
    tickValueUsd: 1.25,
    session: EQ,
  },
  {
    root: "RTY",
    name: "E-mini Russell 2000",
    exchange: "CME",
    group: "Equity index",
    tickSizeNum: 1,
    tickSizeDen: 10,
    tickValueUsd: 5,
    session: EQ,
  },
  {
    root: "M2K",
    name: "Micro E-mini Russell 2000",
    exchange: "CME",
    group: "Equity index",
    tickSizeNum: 1,
    tickSizeDen: 10,
    tickValueUsd: 0.5,
    session: EQ,
  },
  {
    root: "YM",
    name: "E-mini Dow ($5)",
    exchange: "CBOT",
    group: "Equity index",
    tickSizeNum: 1,
    tickSizeDen: 1,
    tickValueUsd: 5,
    session: EQ,
  },
  {
    root: "MYM",
    name: "Micro E-mini Dow",
    exchange: "CBOT",
    group: "Equity index",
    tickSizeNum: 1,
    tickSizeDen: 1,
    tickValueUsd: 0.5,
    session: EQ,
  },
  {
    root: "CL",
    name: "Crude Oil (WTI)",
    exchange: "NYMEX",
    group: "Energy",
    tickSizeNum: 1,
    tickSizeDen: 100,
    tickValueUsd: 10,
    session: GLOBEX,
  },
  {
    root: "MCL",
    name: "Micro WTI Crude Oil",
    exchange: "NYMEX",
    group: "Energy",
    tickSizeNum: 1,
    tickSizeDen: 100,
    tickValueUsd: 1,
    session: GLOBEX,
  },
  {
    root: "NG",
    name: "Henry Hub Natural Gas",
    exchange: "NYMEX",
    group: "Energy",
    tickSizeNum: 1,
    tickSizeDen: 1000,
    tickValueUsd: 10,
    session: GLOBEX,
  },
  {
    root: "GC",
    name: "Gold",
    exchange: "COMEX",
    group: "Metals",
    tickSizeNum: 1,
    tickSizeDen: 10,
    tickValueUsd: 10,
    session: GLOBEX,
  },
  {
    root: "MGC",
    name: "Micro Gold",
    exchange: "COMEX",
    group: "Metals",
    tickSizeNum: 1,
    tickSizeDen: 10,
    tickValueUsd: 1,
    session: GLOBEX,
  },
  {
    root: "SI",
    name: "Silver",
    exchange: "COMEX",
    group: "Metals",
    tickSizeNum: 5,
    tickSizeDen: 1000,
    tickValueUsd: 25,
    session: GLOBEX,
  },
  {
    root: "HG",
    name: "Copper",
    exchange: "COMEX",
    group: "Metals",
    tickSizeNum: 5,
    tickSizeDen: 10000,
    tickValueUsd: 12.5,
    session: GLOBEX,
  },
  {
    root: "ZB",
    name: "U.S. Treasury Bond",
    exchange: "CBOT",
    group: "Rates",
    tickSizeNum: 1,
    tickSizeDen: 32,
    tickValueUsd: 31.25,
    session: "Sun–Fri 17:00–16:00 CT",
  },
  {
    root: "ZN",
    name: "10-Year T-Note",
    exchange: "CBOT",
    group: "Rates",
    tickSizeNum: 1,
    tickSizeDen: 64,
    tickValueUsd: 15.625,
    session: "Sun–Fri 17:00–16:00 CT",
  },
  {
    root: "6E",
    name: "Euro FX",
    exchange: "CME",
    group: "FX",
    tickSizeNum: 5,
    tickSizeDen: 100000,
    tickValueUsd: 6.25,
    session: GLOBEX,
  },
  // Added 2026-09-28 for the symbol dropdown's Common futures (owner). Contract specs from the
  // exchanges' public contract pages, display only [I]: the live InstrumentInfo always wins.
  {
    root: "RB",
    name: "RBOB Gasoline",
    exchange: "NYMEX",
    group: "Energy",
    tickSizeNum: 1,
    tickSizeDen: 10000,
    tickValueUsd: 4.2,
    session: GLOBEX,
  },
  {
    root: "HO",
    name: "NY Harbor ULSD (Heating Oil)",
    exchange: "NYMEX",
    group: "Energy",
    tickSizeNum: 1,
    tickSizeDen: 10000,
    tickValueUsd: 4.2,
    session: GLOBEX,
  },
  {
    root: "SIL",
    name: "Micro Silver",
    exchange: "COMEX",
    group: "Metals",
    tickSizeNum: 5,
    tickSizeDen: 1000,
    tickValueUsd: 5,
    session: GLOBEX,
  },
  {
    root: "PL",
    name: "Platinum",
    exchange: "NYMEX",
    group: "Metals",
    tickSizeNum: 1,
    tickSizeDen: 10,
    tickValueUsd: 5,
    session: GLOBEX,
  },
  {
    root: "ZF",
    name: "5-Year T-Note",
    exchange: "CBOT",
    group: "Rates",
    tickSizeNum: 1,
    tickSizeDen: 128,
    tickValueUsd: 7.8125,
    session: "Sun–Fri 17:00–16:00 CT",
  },
  {
    root: "ZT",
    name: "2-Year T-Note",
    exchange: "CBOT",
    group: "Rates",
    tickSizeNum: 1,
    tickSizeDen: 256,
    tickValueUsd: 7.8125,
    session: "Sun–Fri 17:00–16:00 CT",
  },
  {
    root: "UB",
    name: "Ultra U.S. Treasury Bond",
    exchange: "CBOT",
    group: "Rates",
    tickSizeNum: 1,
    tickSizeDen: 32,
    tickValueUsd: 31.25,
    session: "Sun–Fri 17:00–16:00 CT",
  },
  {
    root: "6J",
    name: "Japanese Yen",
    exchange: "CME",
    group: "FX",
    tickSizeNum: 5,
    tickSizeDen: 10000000,
    tickValueUsd: 6.25,
    session: GLOBEX,
  },
  {
    root: "6B",
    name: "British Pound",
    exchange: "CME",
    group: "FX",
    tickSizeNum: 1,
    tickSizeDen: 10000,
    tickValueUsd: 6.25,
    session: GLOBEX,
  },
  {
    root: "6A",
    name: "Australian Dollar",
    exchange: "CME",
    group: "FX",
    tickSizeNum: 5,
    tickSizeDen: 100000,
    tickValueUsd: 5,
    session: GLOBEX,
  },
  {
    root: "6C",
    name: "Canadian Dollar",
    exchange: "CME",
    group: "FX",
    tickSizeNum: 5,
    tickSizeDen: 100000,
    tickValueUsd: 5,
    session: GLOBEX,
  },
  {
    root: "6S",
    name: "Swiss Franc",
    exchange: "CME",
    group: "FX",
    tickSizeNum: 5,
    tickSizeDen: 100000,
    tickValueUsd: 6.25,
    session: GLOBEX,
  },
  {
    root: "ZC",
    name: "Corn",
    exchange: "CBOT",
    group: "Ags",
    tickSizeNum: 1,
    tickSizeDen: 4,
    tickValueUsd: 12.5,
    session: AGS,
  },
  {
    root: "ZS",
    name: "Soybeans",
    exchange: "CBOT",
    group: "Ags",
    tickSizeNum: 1,
    tickSizeDen: 4,
    tickValueUsd: 12.5,
    session: AGS,
  },
  {
    root: "ZW",
    name: "Chicago SRW Wheat",
    exchange: "CBOT",
    group: "Ags",
    tickSizeNum: 1,
    tickSizeDen: 4,
    tickValueUsd: 12.5,
    session: AGS,
  },
  {
    root: "LE",
    name: "Live Cattle",
    exchange: "CME",
    group: "Ags",
    tickSizeNum: 1,
    tickSizeDen: 40,
    tickValueUsd: 10,
    session: LIVESTOCK,
  },
  {
    root: "HE",
    name: "Lean Hogs",
    exchange: "CME",
    group: "Ags",
    tickSizeNum: 1,
    tickSizeDen: 40,
    tickValueUsd: 10,
    session: LIVESTOCK,
  },
  {
    root: "MBT",
    name: "Micro Bitcoin",
    exchange: "CME",
    group: "Crypto",
    tickSizeNum: 5,
    tickSizeDen: 1,
    tickValueUsd: 0.5,
    session: GLOBEX,
  },
  {
    root: "MET",
    name: "Micro Ether",
    exchange: "CME",
    group: "Crypto",
    tickSizeNum: 1,
    tickSizeDen: 2,
    tickValueUsd: 0.05,
    session: GLOBEX,
  },
];

const BY_ROOT = new Map(FUTURES_CATALOG.map((e) => [e.root, e]));

/** CME month codes (F Jan … Z Dec). */
export const MONTH_CODES = "FGHJKMNQUVXZ";

/** "NQZ6" / "NQZ26" → { root "NQ", month "Z", year "6" }; null for a bare root or unknown text. */
export function parseContract(symbol: string): { root: string; month: string; year: string } | null {
  const s = symbol.trim().toUpperCase();
  const m = /^([A-Z0-9]+?)([FGHJKMNQUVXZ])(\d{1,2})$/.exec(s);
  if (!m?.[1] || !m[2] || !m[3]) return null;
  return BY_ROOT.has(m[1]) ? { root: m[1], month: m[2], year: m[3] } : null;
}

/** The catalog entry for a root or a contract of it ("NQ", "NQZ6"). */
export function catalogEntry(symbol: string | null): CatalogEntry | null {
  if (symbol === null) return null;
  const s = symbol.trim().toUpperCase();
  return BY_ROOT.get(s) ?? BY_ROOT.get(parseContract(s)?.root ?? "") ?? null;
}

export type SymbolMatch = {
  /** What gets added / loaded: a root (front month, resolved live) or an explicit contract. */
  symbol: string;
  entry: CatalogEntry;
  kind: "root" | "contract";
};

/**
 * Search the catalog (watchlist "+" box). Ranking: exact root, then root prefix, then every query
 * word starting a word of the name ("micro dow"), then the name containing every word; roots in
 * catalog order within a rank. A query that is a contract of a known root ("NQH7") comes first as that contract, then
 * its root (front month). Empty query → the first `limit` roots.
 */
export function searchSymbols(query: string, limit = 12): SymbolMatch[] {
  const q = query.trim().toUpperCase();
  if (!q)
    return FUTURES_CATALOG.slice(0, limit).map((entry) => ({ symbol: entry.root, entry, kind: "root" }));
  const out: SymbolMatch[] = [];
  const c = parseContract(q);
  if (c) {
    const entry = BY_ROOT.get(c.root);
    if (entry) out.push({ symbol: q, entry, kind: "contract" });
  }
  const tokens = q.split(/\s+/);
  const rank = (e: CatalogEntry): number => {
    const name = e.name.toUpperCase();
    if (e.root === q || e.root === c?.root) return 0;
    if (e.root.startsWith(q)) return 1;
    const words = [e.root, ...name.split(/[\s\-()$&.]+/)];
    // Every query word starts a word of the name ("micro dow", "gold", "e-mini s").
    if (tokens.every((t) => words.some((w) => w.startsWith(t)))) return 2;
    if (tokens.every((t) => name.includes(t))) return 3;
    return -1;
  };
  const ranked = FUTURES_CATALOG.map((e, i) => ({ e, i, r: rank(e) }))
    .filter((x) => x.r >= 0)
    .sort((a, b) => a.r - b.r || a.i - b.i);
  for (const { e } of ranked) out.push({ symbol: e.root, entry: e, kind: "root" });
  return out.slice(0, limit);
}
